import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';

import { requestConfirmBuyPasses } from 'network/services/payments.service';
import {
  readPendingConektaOrderId,
  clearPendingConektaOrderId,
} from 'features/payments/utils/conekta-pending-order.util';

// Cierra la compra cuando Conekta resolvió el 3DS navegando la ventana entera a la
// `return_url` con `?payment_status=...`. El modal y su sondeo se destruyeron con la
// navegación, así que sin esto la compra quedaría cobrada y sin pases hasta que el
// usuario recargara.
//
// Como todo lo demás, no confía en el query param: solo lo usa como señal para
// preguntarle al backend, que re-consulta la orden contra Conekta.
//
// Se monta una vez en App.js, así que funciona en cualquier ruta de regreso.

// Conekta redirige en cuanto el cardholder termina el reto, pero su orden tarda
// un poco más en quedar `paid`. Confirmar una sola vez, justo al volver, cae en
// ese hueco y reporta "pendiente" una compra que sí entró.
//
// La ventana es corta a propósito: aquí no se espera a que alguien teclee un SMS
// —eso ya ocurrió— sino a que Conekta cierre, que es cosa de segundos. Si aun así
// no cierra, el webhook otorga los pases de todos modos.
const CONFIRM_RETRY_DELAY_MS = 2000;
const MAX_CONFIRM_ATTEMPTS = 10; // ~20 segundos

export const useConektaRedirectReturn = () => {
  const { search } = useLocation();
  const { replace } = useHistory();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // El efecto depende de `search`, que cambia al limpiar la URL; esto evita
  // reentrar mientras la confirmación sigue en vuelo.
  const isHandlingRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const paymentStatus = params.get('payment_status');

    if (paymentStatus === null || isHandlingRef.current) return undefined;

    const orderId = readPendingConektaOrderId();

    // Limpia el query param siempre, haya o no orden pendiente, para que recargar no
    // vuelva a disparar esto.
    const cleanUrl = () => {
      params.delete('payment_status');
      const rest = params.toString();
      replace({ search: rest ? `?${rest}` : '' });
    };

    if (!orderId) {
      cleanUrl();
      return undefined;
    }

    isHandlingRef.current = true;

    let isCancelled = false;
    let timeoutId = null;

    const wait = ms =>
      new Promise(resolve => {
        timeoutId = setTimeout(resolve, ms);
      });

    const confirmWithRetries = async () => {
      for (let attempt = 1; attempt <= MAX_CONFIRM_ATTEMPTS; attempt += 1) {
        if (isCancelled) return;

        try {
          const response = await requestConfirmBuyPasses({ orderId });
          if (isCancelled) return;

          if (!response || !response.requiresAction) {
            clearPendingConektaOrderId();
            queryClient.invalidateQueries('GET_PASSES');
            enqueueSnackbar('Se realizó la compra con éxito', {
              variant: 'success',
            });
            return;
          }
        } catch (error) {
          if (isCancelled) return;

          // Un fallo de red a mitad del reintento no significa que la compra
          // fallara; solo se reporta si fue el último intento.
          if (attempt === MAX_CONFIRM_ATTEMPTS) {
            clearPendingConektaOrderId();
            enqueueSnackbar('No se pudo confirmar el pago. Intenta más tarde', {
              variant: 'error',
            });
            return;
          }
        }

        if (attempt < MAX_CONFIRM_ATTEMPTS) {
          await wait(CONFIRM_RETRY_DELAY_MS);
        }
      }

      if (isCancelled) return;

      // Se agotó la ventana y Conekta sigue pidiendo verificación. No se puede
      // re-presentar el reto después de haber perdido la ventana, así que se le
      // dice al usuario en vez de fingir éxito. La orden sigue viva y el webhook
      // la cerrará si llega a pagarse; por eso tampoco se borra la orden
      // pendiente.
      enqueueSnackbar(
        'La verificación quedó pendiente. Revisa tus pases antes de volver a intentar',
        { variant: 'warning' },
      );
    };

    // `cleanUrl` va al final a propósito. Cambia `search`, que es la dependencia
    // de este efecto, así que React ejecuta la limpieza de abajo y cancelaría el
    // bucle de reintentos antes de que alcance a notificar. Mientras tanto el
    // query param se queda en la URL unos segundos, que es inofensivo.
    confirmWithRetries().finally(() => {
      isHandlingRef.current = false;
      cleanUrl();
    });

    return () => {
      isCancelled = true;
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
};
